/**
 * The platform's ONE editable grid — every editable surface renders it.
 *
 * ═══ THE ONE LAW ═════════════════════════════════════════════════
 *  Every way of LEAVING a cell COMMITS the value. Keys differ only in
 *  where focus goes next. Discarding is Escape, and Escape alone.
 * ═════════════════════════════════════════════════════════════════
 *
 * Each row below is enforced by a case in
 * `web/src/modules/grids/contract/keymap.contract.test.tsx` — change the
 * behavior and that spec fails. `⚠` marks a KNOWN-OPEN defect that the
 * spec pins with `test.fails`; do not read it as working.
 *
 *  LEAVING A CELL
 *  ┌─────────────┬────────────────────────────┬──────────────────────┐
 *  │ Tab         │ commit → next EDITABLE     │ skips read-only,     │
 *  │             │   cell                     │ wraps to next row    │
 *  │ Shift+Tab   │ commit → previous editable │ first cell: commits  │
 *  │             │                            │ in place             │
 *  │ Enter       │ commit → DOWN, same column │ ONE press, incl. an  │
 *  │             │                            │ open autocomplete    │
 *  │ Shift+Enter │ commit → UP, same column   │ top row: in place    │
 *  │ Escape      │ DISCARD → stay, view mode  │ the ONLY discard     │
 *  │ click-away  │ commit — never discards    │                      │
 *  └─────────────┴────────────────────────────┴──────────────────────┘
 *  ⚠ a value typed in the SAME TICK as Tab/Shift+Tab is still dropped
 *    in PLAIN editors (MUI's 200ms setEditCellValue debounce has not
 *    flushed). Autocompletes are fixed; text/number are not.
 *
 *  ENTERING EDIT
 *    single-click  opens the row's editors  (read-only cells refuse)
 *    type-over     a printable key on a focused cell starts editing
 *    canEdit=false NOTHING enters edit — no key, no click; an editor
 *                  already open is force-exited and its edit DISCARDED
 *
 *  EDITOR-SPECIFIC — the only sanctioned deviations
 *    autocomplete  Tab/Enter commit the SELECTION · Escape discards
 *                  ⚠ the highlight is derived from the UNFILTERED
 *                    option list, so a filtered list can commit an
 *                    option the user never saw
 *    checkbox      Space TOGGLES and stays in edit (ARIA); Tab/Enter
 *                  still commit and move. Space in text is a character.
 *    wire          ONE mutation per leave
 *                  ⚠ a fieldless second `update` can still escape at
 *                    the unpack boundary (the `_action` echo)
 *
 *  NAVIGATION (view mode) — native MUI, deliberately untouched
 *    Arrows · Home/End · Ctrl+Home/End · PageUp/PageDown
 *
 *  RULED, NOT YET BUILT (Deep, 2026-08-22)
 *    R-A  add-row pressed twice refocuses the untouched draft
 *         instead of minting a second one
 *    R-B  arriving in a cell via Enter OPENS its editor — the
 *         Sheets rapid-entry cascade
 */
import { Box } from '@mui/system';
import {
  GridApiPremium,
  GridCellParams,
  GridCellProps,
  GridRowHeightParams,
  GridRowId,
  type GridRowModelUpdate,
  GridRowModes,
  type GridValidRowModel,
  gridEditRowsStateSelector,
  MuiEvent,
} from '@mui/x-data-grid-premium';
import { RiAddLine } from '@remixicon/react';
import type { Decision } from '@shared/core/policy';
import { isEqual, mapValues, sortBy } from 'lodash';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import GridActionsToolbar from 'src/components/atoms/grid/GridActionsToolbar';
import { AgButton, PolicyLockHint } from 'src/components/ui';
import { RequestQueue } from 'src/hooks/useQueuedRequests';
import { GridRow, GridValidationModel, isDraftAwaitingCreate } from 'src/lib/gridRows.store';
import { MIN_ROW_HEIGHT } from 'src/utils/grid/gridStyles';
import { useEffectOnce } from 'usehooks-ts';
import DataGrid, { DataGridProps, type GridApiRef } from '../DataGrid';
import { useColumnCellEditability, useEnterEditCascade, useGridKeyboardNav, useSingleClickEdit } from '../keyboard';
import EditableGridCell, { EDITABLE_GRID_CELL_STYLE } from './EditableGridCell';
import { EditableGridToolbar } from './EditableGridToolbar';
import { useEditableGridRows } from './useEditableGridRows';
import { type GridRowSaveChangesReason, useGridSavePipeline } from './useGridSavePipeline';

export type EditableDataGridProps<Row extends GridValidRowModel, Input> = {
  /**
   * Create an empty row locally, ready for user input.
   */
  createFreshRow: (source?: Partial<Row>) => Row;
  title?: string;
  /** Optional banner content to display between the title row and the grid */
  bannerContent?: React.ReactNode;
  /** Layer for the floating cell editor. Defaults to `theme.zIndex.drawer - 1`. */
  editorZIndex?: number;

  onCreate: (payload: { input: Input }) => Promise<Row>;
  onEdit: (payload: { id: number; input: Input }) => Promise<Row>;
  onMutationResolved?: (previousRow: GridRow<Row>, acceptedRow: Partial<Row>, resolvedRow: GridRow<Row>) => void;
  rowToInput: (changes: Partial<GridRow<Row>>, row: GridRow<Row>) => Input;
  /**
   * READ-ONLY ENFORCEMENT — one switch, five gates. Consumers PASS this; they
   * never re-implement read-only per surface.
   *
   *   canEdit={false}
   *      ├─ isCellEditable ─────────╳──▶ no cell is ever editable
   *      ├─ single-click entry ─────╳──▶ never opens row edit mode
   *      ├─ canAdd ─────────────────╳──▶ no add button, no Tab-to-new-row
   *      ├─ disabledColumns.actions ╳──▶ no delete / duplicate affordance
   *      └─ open editors ───────────╳──▶ force-exited, changes DISCARDED
   *
   * The first four gate ENTRY; the fifth closes what was already open when
   * the flip landed (reactive — see the effect below).
   *
   * Composition, not replacement: with `canEdit` true a consumer-supplied
   * `isCellEditable` still decides; with it false the consumer callback cannot
   * re-open a cell. Defaults to `true`, so grids that never pass it are
   * unchanged.
   */
  canEdit?: boolean;
  /**
   * Defaults to `canEdit`. `canEdit={false}` forces this off — a read-only grid
   * has no add affordance even if a caller asks for one.
   */
  canAdd?: boolean;
  /**
   * Optional policy result for Add. Denial keeps the affordance visible with
   * its reason while every draft-creation path is stopped before mutation.
   */
  addDecision?: Decision;
  newRowLabel: string;
  handleRequest: RequestQueue<Row, Input, 'create' | 'update'>;
  /**
   * One schema for every row, OR a function of the row:
   *
   *   validationModel={schema}                      ── static
   *   validationModel={({ row, rowNode }) => schema} ── row-dependent
   *
   * The function form is resolved per row, on the mount-time sweep and on every
   * save alike; `rowNode` is bound here from `apiRef` so depth-based models
   * (tree grids) see it in both paths.
   */
  validationModel: GridValidationModel<Record<string, unknown>>;
  /** If provided, this function will allow the component above to process the changes.
   * The default behavior will only execute if the result of this function call is undefined.
   */
  saveChanges?: (row: GridRow<Row>, reason: GridRowSaveChangesReason) => GridRow<Row> | undefined;
  afterSaveChanges?: (row: GridRow<Row>) => void;
  toolbarButtons?: React.ReactNode;
  disabledColumns?: Record<string, boolean>;
  apiRef: GridApiRef;
  /**
   * When provided, shows a header above the grid with "{count} {label}s" and column visibility toggle.
   * E.g., "product" -> "5 products"
   */
  rowCountLabel?: string;
  /**
   * Mint a starter draft when the grid mounts with no rows. TRUE by default: on entry surfaces the
   * ready row is the point. Surfaces where a blank row reads as real data — an invoice's charges —
   * opt out, or the grid invents a line nobody created.
   */
  autoAddFirstRow?: boolean;
  /**
   * Override the row count displayed in the header. If not provided, counts non-draft rows.
   */
  rowCount?: number;
} & DataGridProps<GridRow<Row>>;

const ROW_HEIGHT_PX = 48;
const AUTO_HEIGHT_MAX_ROWS = 15;
const mountedEditableGridSlices = new Map<object, number>();
const SHARED_SLICE_ERROR =
  '[EditableDataGrid] Two grids are sharing one editableGridInternal slice — wrap each grid in a StoreStateLensFocusProvider with a distinct slice key (silent state-sharing bug).';

export const EditableDataGrid = function <R extends GridValidRowModel = GridValidRowModel, I = object>({
  title,
  bannerContent,
  createFreshRow,
  apiRef,
  onCreate,
  onEdit,
  onMutationResolved,
  rowToInput,
  newRowLabel,
  handleRequest,
  validationModel,
  getRowId: _getRowId,
  getRowHeight: _getRowHeight,
  saveChanges: _saveChanges,
  afterSaveChanges,
  disabledColumns: _disabledColumns,
  canEdit = true,
  canAdd: _canAdd,
  addDecision,
  isCellEditable: _isCellEditable,
  toolbarButtons,
  gridKey,
  storageKey,
  gridId,
  rowCountLabel,
  autoAddFirstRow = true,
  rowCount: rowCountProp,
  editorZIndex,
  ...props
}: EditableDataGridProps<R, I>) {
  const reactId = useId();
  const instanceGridId = gridId ? `${gridId}-${reactId}` : reactId;
  const initialRows = useEditableGridRows((store) => store.initialRows);
  const getLocalRows = useEditableGridRows((store) => store.getLocalRows);
  // Tabbed hosts keep the store above the tab, so this grid can REMOUNT over a store whose
  // `initialRows` (rebuilt only on seed/refresh) predates rows created, edited or removed since —
  // their `rowUpdates` were drained into the previous grid. Mount from the live rows, in seed
  // order, and follow `initialRows` again once the store rebuilds it.
  const [mountSeed] = useState(() => {
    const seedOrder = new Map(initialRows.map((row, index) => [row.rowId, index]));
    const liveRows = sortBy(getLocalRows(), (row) => seedOrder.get(row.rowId) ?? Number.POSITIVE_INFINITY);
    return { initialRows, rows: liveRows };
  });
  const rows = initialRows === mountSeed.initialRows ? mountSeed.rows : initialRows;
  const validateRows = useEditableGridRows((store) => store.validateRows);
  const addDraft = useEditableGridRows((store) => store.addDraft);
  const getRowId = useCallback((row: GridRow<R>) => _getRowId?.(row) ?? row.rowId, [_getRowId]);

  /**
   * ── canEdit gates ────────────────────────────────────────────────────────
   * Each derivation COMPOSES with what the consumer passed; none replaces it.
   * `canEdit` defaults to true, so every expression below is identity for the
   * grids that never pass it.
   *
   *   canAdd            canEdit && (consumer canAdd ?? true)
   *   isCellEditable    canEdit && (consumer isCellEditable ?? true)
   *   disabledColumns   read-only pins `actions: true` LAST — the core wins,
   *                     so no consumer can re-open a delete affordance on a
   *                     read-only grid (this is the point of the ruling).
   *
   * Whenever the grid HAS an actions column the key is emitted in BOTH
   * directions: `disabledColumns` is the override `DataGrid` re-applies over
   * the user's visibility model, so dropping the key on the editable side
   * leaves a persisted `actions: false` unopposed.
   */
  const canAdd = canEdit && (_canAdd ?? true);
  const canCreateRow = canAdd && addDecision?.allowed !== false;

  const isCellEditable = useCallback<NonNullable<EditableDataGridProps<R, I>['isCellEditable']>>(
    (params) => (canEdit ? (_isCellEditable?.(params) ?? true) : false),
    [canEdit, _isCellEditable]
  );

  const hasActionsColumn = 'actions' in (props.columns ?? {});

  const disabledColumns = useMemo(() => {
    if (!hasActionsColumn) {
      return _disabledColumns;
    }

    return canEdit
      ? { ..._disabledColumns, actions: _disabledColumns?.actions ?? false }
      : { ..._disabledColumns, actions: true };
  }, [canEdit, hasActionsColumn, _disabledColumns]);

  /**
   * ── the fifth gate: canEdit true ──▶ false with a row ALREADY OPEN ───────
   * The four gates above stop edit ENTRY; none of them closes an editor that
   * was open when authority was revoked. Left alone it commits later:
   *
   *   canEdit true          false                      commit
   *   ──── row 3 open ────────╳──── row 3 STILL open ────▶ save vs locked
   *        (entry gated)      ▲                            order ──▶ 4xx
   *                           └─ the flip the user cannot see
   *
   * DISCARD, never commit (`ignoreModifications`): the flip revokes the
   * right to write, so the in-flight value has no claim on the server — a
   * doomed commit would manufacture the exact error the lock prevents.
   *
   *   true ──▶ false   every open row ──▶ View, edits dropped
   *   false ──▶ true   nothing reopens — entry stays user-driven
   */
  const wasEditableRef = useRef(canEdit);

  useEffect(() => {
    const wasEditable = wasEditableRef.current;
    wasEditableRef.current = canEdit;

    if (canEdit || !wasEditable) {
      return;
    }

    const api = apiRef.current;
    if (!api) return;

    const editRows = gridEditRowsStateSelector(apiRef);

    // No open editor ⇒ nothing to close. Keeps the flip a true no-op instead
    // of walking every row id on grids that were merely idle.
    if (!editRows || Object.keys(editRows).length === 0) {
      return;
    }

    // Walk real row ids, not the string keys of `editRows` — `stopRowEditMode`
    // matches on the row id's own type and numeric ids would miss.
    api.getAllRowIds().forEach((rowId) => {
      if (api.getRowMode(rowId) === GridRowModes.Edit) {
        api.stopRowEditMode({ id: rowId, ignoreModifications: true });
      }
    });
  }, [canEdit, apiRef]);

  const boundValidationModel = useMemo<GridValidationModel<Record<string, unknown>>>(() => {
    if (typeof validationModel !== 'function') {
      return validationModel;
    }

    return (context) =>
      validationModel({ ...context, rowNode: apiRef.current?.getRowNode(context.row.rowId) ?? undefined });
  }, [validationModel, apiRef]);

  const { rowModesModel, handleCellClick, handleRowModesModelChange, setRowModesModel } = useSingleClickEdit(apiRef, {
    canEdit,
  });

  const localRows = useEditableGridRows((store) => store.getLocalRows());

  // The starter draft is decided ONCE, the first time the grid can create a row — at mount, or
  // later when a section gate ANDed into `canEdit` opens (SO Products/Charges, PO, BSO, GP).
  // Deciding at mount only left those grids at "No rows". Never re-decided, so deleting the
  // last row or re-gating does not resurrect a draft.
  const starterDraftDecidedRef = useRef(false);

  useEffect(() => {
    if (starterDraftDecidedRef.current || !canCreateRow) {
      return;
    }

    starterDraftDecidedRef.current = true;
    if (autoAddFirstRow && getLocalRows().length === 0) {
      const newRow = addDraft(createFreshRow());
      apiRef.current?.updateRows([newRow]);
    }
  }, [canCreateRow, autoAddFirstRow, getLocalRows, addDraft, createFreshRow, apiRef]);

  useEffectOnce(() => {
    validateRows(boundValidationModel);
  });

  const { processRowUpdate } = useGridSavePipeline<R, I>(apiRef, {
    afterSaveChanges,
    getRowId,
    handleRequest,
    onCreate,
    onEdit,
    onMutationResolved,
    rowToInput,
    saveChanges: _saveChanges,
    setRowModesModel,
    validationModel: boundValidationModel,
  });

  const { handleCellDoubleClick } = useColumnCellEditability(apiRef);
  useGridKeyboardNav<R>(apiRef, { canAdd: canCreateRow, createFreshRow, instanceGridId });
  // R-B: Enter commits, moves DOWN, and OPENS what it arrives in (Sheets cascade).
  useEnterEditCascade(apiRef, { canEdit });

  const rowCount = localRows.length;
  const autoHeight = rowCount <= AUTO_HEIGHT_MAX_ROWS;
  const estimatedHeight = `${ROW_HEIGHT_PX * AUTO_HEIGHT_MAX_ROWS}px`; // todo, support densityFactor. Standard = 1

  const sx = useMemo(
    () => ({
      ...EDITABLE_GRID_CELL_STYLE,
      '& .MuiDataGrid-main': {
        borderRadius: canAdd ? '10px 10px 0 0' : '10px',
        outline: '1px solid var(--color-neutral-5)',
      },
      ...props.sx,
    }),
    [canAdd, props.sx]
  );

  // The count includes an OPEN DRAFT row: it is painted like any other row, so excluding it
  // made the header contradict the grid ("0 expenses" above a visible blank charge).
  const displayRowCount = rowCountProp ?? localRows.length;

  // Callers build their model inline; key it by content so the toolbar slot (and an open menu) survives re-renders.
  const controlledVisibilityKey = JSON.stringify(props.columnVisibilityModel ?? {});
  const controlledVisibility = useMemo<Record<string, boolean>>(
    () => JSON.parse(controlledVisibilityKey),
    [controlledVisibilityKey]
  );
  const toolbar = useCallback(
    () => (
      <EditableGridToolbar
        title={title}
        rowCountLabel={rowCountLabel}
        displayRowCount={displayRowCount}
        toolbarButtons={toolbarButtons}
        disabledColumns={disabledColumns}
        controlledVisibility={controlledVisibility}
        bannerContent={bannerContent}
      />
    ),
    [title, toolbarButtons, disabledColumns, controlledVisibility, rowCountLabel, displayRowCount, bannerContent]
  );

  const editableGridCell = useCallback(
    (cellProps: GridCellProps) => (
      <EditableGridCell {...cellProps} instanceGridId={instanceGridId} editorZIndex={editorZIndex} />
    ),
    [instanceGridId, editorZIndex]
  );

  const slots = useMemo(
    () => ({ ...props.slots, cell: editableGridCell, toolbar }),
    [editableGridCell, toolbar, props.slots]
  );

  const onKeyDown = useCallback(
    (_: GridCellParams, e: MuiEvent<React.KeyboardEvent<HTMLElement>>) => {
      if (e.key !== 'Tab') {
        return;
      }

      // Only prevent Tab if the active element is within this grid instance
      const gridRoot = apiRef.current?.rootElementRef?.current;
      if (!gridRoot?.contains(document.activeElement)) {
        return;
      }

      e.preventDefault();
      e.defaultMuiPrevented = true;
    },
    [apiRef]
  );

  const getRowHeight = useCallback(
    (params: GridRowHeightParams) => _getRowHeight?.(params) ?? ROW_HEIGHT_PX * params.densityFactor,
    [_getRowHeight]
  );

  const syncRowUpdates = useEditableGridRows((store) => store.syncRowUpdates());

  useEffect(() => {
    if (syncRowUpdates.rowUpdates?.length) {
      const updates: GridRowModelUpdate[] = syncRowUpdates.rowUpdates.map((row) => {
        // The store's create/update/restore marker is also row data used by the
        // diff guard. MUI treats only the literal "delete" as an operation.
        const update: GridValidRowModel = row;
        return update;
      });
      apiRef.current?.updateRows(updates);
      syncRowUpdates.reset();
    }
  }, [syncRowUpdates, apiRef]);

  return (
    <>
      {process.env.NODE_ENV === 'development' && <EditableGridSliceCollisionGuard />}
      <GridActionsToolbar apiRef={apiRef} />
      <DataGrid<GridRow<R>>
        {...props}
        apiRef={apiRef}
        rowModesModel={rowModesModel}
        onCellClick={handleCellClick}
        onCellDoubleClick={handleCellDoubleClick}
        getRowId={getRowId}
        onRowModesModelChange={handleRowModesModelChange}
        rows={rows}
        autoHeight={autoHeight}
        sx={sx}
        height={autoHeight ? '100%' : estimatedHeight}
        processRowUpdate={processRowUpdate}
        editMode="row"
        slots={slots}
        hideFooter
        onCellKeyDown={onKeyDown}
        getRowHeight={getRowHeight}
        canEdit={canEdit}
        isCellEditable={isCellEditable}
        disabledColumns={disabledColumns}
        gridKey={gridKey}
        storageKey={storageKey}
        gridId={gridId}
      />
      {canAdd && (
        <Box
          sx={{
            border: '1px solid var(--color-neutral-5)',
            margin: rowCountLabel ? '-1px -1px 0 -1px' : '0 -1px',
            borderTop: 0,
            borderRadius: '0 0 10px 10px',
            minHeight: MIN_ROW_HEIGHT,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <EnterNewRowButton label={newRowLabel} createRow={createFreshRow} apiRef={apiRef} decision={addDecision} />
        </Box>
      )}
    </>
  );
};

function EditableGridSliceCollisionGuard() {
  const editableGridSliceAtMount = useRef(useEditableGridRows((state) => state)).current;
  const editableGridSliceIdentity = editableGridSliceAtMount.addDraft;

  useEffect(() => {
    const mountedCount = mountedEditableGridSlices.get(editableGridSliceIdentity) ?? 0;
    mountedEditableGridSlices.set(editableGridSliceIdentity, mountedCount + 1);

    if (mountedCount >= 1) {
      console.error(SHARED_SLICE_ERROR);
    }

    return () => {
      const currentCount = mountedEditableGridSlices.get(editableGridSliceIdentity);

      if (currentCount === undefined || currentCount <= 1) {
        mountedEditableGridSlices.delete(editableGridSliceIdentity);
        return;
      }

      mountedEditableGridSlices.set(editableGridSliceIdentity, currentCount - 1);
    };
  }, [editableGridSliceIdentity]);

  return null;
}

/**
 * Store bookkeeping — never part of "did the user touch this draft?". `id` is
 * here because the store, not the user, fills it in on reconcile.
 */
const STORE_OWNED_ROW_KEYS = new Set(['rowId', 'status', 'id', 'isDraft', 'isDeleted', 'isCreating']);

const draftFields = (row: object): Record<string, unknown> =>
  Object.fromEntries(Object.entries(row).filter(([field]) => !STORE_OWNED_ROW_KEYS.has(field)));

/**
 * Sentinel for a cell the grid could not resolve. Never equal to anything a user can type, so
 * an unreadable column reads as TOUCHED — costing a spare row rather than the row someone was
 * filling in.
 */
const UNREADABLE_CELL = Symbol('unreadable-cell');

/** `getCellValue` throws `MissingRowIdError` for a row MUI's own model has never held. */
const cellValue = (api: GridApiPremium, rowId: GridRowId, field: string): unknown => {
  try {
    return api.getCellValue(rowId, field);
  } catch {
    return UNREADABLE_CELL;
  }
};

const pendingEditorValues = (
  apiRef: GridApiRef,
  rowId: GridRowId
): Record<string, { editor: unknown; cell: unknown }> => {
  const api = apiRef.current;
  const editRows = api ? gridEditRowsStateSelector(apiRef) : undefined;
  const cells = editRows?.[rowId];

  return api && cells
    ? mapValues(cells, (cell, field) => ({ editor: cell?.value, cell: cellValue(api, rowId, field) }))
    : {};
};

/**
 * A draft is fresh only if the store row is `isEqual` to a CURRENT `createRow()` AND every open
 * MUI editor still holds the row's own value: pressing Add blurs the editor and that commit is
 * ASYNC, so a store-only check hands a half-filled row back as empty. `isEqual` keeps `0`,
 * `''`, `null` and `undefined` four distinct values.
 */
const isUntouchedDraft = (
  row: GridRow<object>,
  freshRow: object,
  pendingValues: Record<string, { editor: unknown; cell: unknown }>
): boolean => {
  if (!isDraftAwaitingCreate(row) || row.isDeleted) {
    return false;
  }

  if (!isEqual(draftFields(row), draftFields(freshRow))) {
    return false;
  }

  return Object.values(pendingValues).every(({ editor, cell }) => isEqual(editor, cell));
};

/**
 * Past 15 rows the grid is a fixed-height window and a new draft lands below it. Focus does not
 * scroll, so without this the portaled editor follows the hidden row out over the next section.
 * A row added this tick has no measured position yet (`scrollToIndexes` returns false), so the
 * scroll is retried once the grid has laid the row out.
 */
const scrollRowIntoView = (api: GridApiPremium, rowId: GridRowId, field: string): void => {
  const scroll = (): boolean => {
    // The retry runs a frame later; the grid may have unmounted (route change) in between.
    if (!api.rootElementRef?.current) return false;
    const rowIndex = api.getRowIndexRelativeToVisibleRows(rowId);
    if (rowIndex === undefined || rowIndex < 0) return false;
    return api.scrollToIndexes({ rowIndex, colIndex: api.getColumnIndexRelativeToVisibleColumns(field) });
  };

  if (!scroll()) {
    requestAnimationFrame(scroll);
  }
};

/** Open the row and put the caret in the first column the user can actually type in. */
const focusFirstEditableCell = (api: GridApiPremium, rowId: GridRowId): void => {
  const firstColumn = api.getVisibleColumns().find((col) => col.editable);

  if (api.getRowMode(rowId) !== GridRowModes.Edit) {
    api.startRowEditMode({ id: rowId });
  }

  if (!firstColumn) {
    return;
  }

  scrollRowIntoView(api, rowId, firstColumn.field);

  const focusedCell = api.state.focus.cell;

  api.setCellFocus(rowId, firstColumn.field);

  // `setCellFocus` returns early on an already-focused cell and then publishes
  // nothing, so MUI's row-edit focus tracker (useGridRowEditing.js) never learns
  // focus came back to this row and stops the row out from under the caret.
  if (focusedCell?.id === rowId && focusedCell.field === firstColumn.field) {
    api.publishEvent('cellFocusIn', api.getCellParams(rowId, firstColumn.field));
  }
};

/**
 * The add affordance — ONE row per gesture (ruling R-A).
 *
 *      press #1              press #2, untouched draft still there
 *   ───────────────    ──────────────────────────────────────────────
 *      mint a draft ──▶  refocus THAT draft · mint nothing · fire nothing
 *      focus it          (draft touched ⇒ mint a second one as before)
 *
 * Legacy `AddableDataGrid` deduped through `draftRowSelector`
 * (AddableDataGrid.tsx:220,:296) and the consolidation dropped it, so every
 * extra press left another empty row behind — the ghost-row class, a cousin of
 * TAS-3271's two-space create. It lives HERE, in the core, so no consumer
 * re-implements it (§B4).
 */
export const EnterNewRowButton = function ({
  label,
  createRow,
  apiRef,
  afterCreateRow,
  decision,
}: {
  label: string;
  createRow: () => object;
  afterCreateRow?: (row: GridRow<object>) => void;
  apiRef: GridApiRef;
  decision?: Decision;
}) {
  const addDraft = useEditableGridRows((store) => store.addDraft);
  const findRow = useEditableGridRows((store) => store.findRow);

  return (
    <PolicyLockHint decision={decision}>
      <AgButton
        variant="ghost"
        className="no-underline! normal-case! text-brand-text! gap-1 justify-start! text-[14px] items-center"
        isDisabled={decision?.allowed === false}
        onPress={() => {
          if (decision?.allowed === false) {
            return;
          }

          const api = apiRef.current;
          if (!api) return;

          const freshRow = createRow();
          const existingDraft = findRow((row) =>
            isUntouchedDraft(row, freshRow, pendingEditorValues(apiRef, row.rowId))
          );
          const row = existingDraft ?? addDraft(freshRow);

          if (!existingDraft) {
            api.updateRows([row]);
          }

          focusFirstEditableCell(api, row.rowId);

          // Fired on BOTH paths: consumers use it to reveal the row (LineItemsGrid
          // expands the parent group), and a refocused draft the user cannot see
          // is not a refocus at all.
          afterCreateRow?.(row);
        }}
        // Read by `useGridSavePipeline`'s cellFocusOut guard to recognise a blur it caused.
        data-grid-add-row
        data-testid="add-row-button"
      >
        <RiAddLine size={18} className="text-brand-text!" />
        {label}
      </AgButton>
    </PolicyLockHint>
  );
};
